import { graphql } from 'react-relay';
import { useIntl } from 'react-intl-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { getTemplatesForTeamType } from '../utils/getTemplatesForTeamType.ts';

import { useShouldShowNewUserTemplates } from './useShouldShowNewUserTemplates.ts';
import type {
	useNewUserTemplatesGetMultipleTemplateInfoQuery,
	useNewUserTemplatesGetMultipleTemplateInfoQueryVariables,
} from './__types__/useNewUserTemplatesGetMultipleTemplateInfoQuery.ts';
import { useTeamType } from './useTeamType.ts';
const templateInfo = graphql`
	query useNewUserTemplatesGetMultipleTemplateInfoQuery(
		$templateId0: ID!
		$templateId1: ID!
		$templateId2: ID!
	) {
		template0: templateInfo(id: $templateId0) {
			templateId
			contentBlueprintId
			blueprintModuleCompleteKey
			itemModuleCompleteKey
			templateType
			name
			description
			iconURL
			darkModeIconURL
			styleClass
			hasWizard
			isNew
			isPromoted
			isFavourite
			hasGlobalBlueprintContent
			categoryIds
			keywords
			recommendationRank
			author
		}
		template1: templateInfo(id: $templateId1) {
			templateId
			contentBlueprintId
			blueprintModuleCompleteKey
			itemModuleCompleteKey
			templateType
			name
			description
			iconURL
			darkModeIconURL
			styleClass
			hasWizard
			isNew
			isPromoted
			isFavourite
			hasGlobalBlueprintContent
			categoryIds
			keywords
			recommendationRank
			author
		}
		template2: templateInfo(id: $templateId2) {
			templateId
			contentBlueprintId
			blueprintModuleCompleteKey
			itemModuleCompleteKey
			templateType
			name
			description
			iconURL
			darkModeIconURL
			styleClass
			hasWizard
			isNew
			isPromoted
			isFavourite
			hasGlobalBlueprintContent
			categoryIds
			keywords
			recommendationRank
			author
		}
	}
`;

export const useNewUserTemplates = () => {
	const { formatMessage } = useIntl();
	const {
		showNewUserTemplates,
		loading: shouldShowLoading,
		error: shouldShowError,
	} = useShouldShowNewUserTemplates();
	const skip = !showNewUserTemplates;
	const { teamType, error: teamTypeError, loading: teamTypeLoading } = useTeamType({ skip });
	const templates = getTemplatesForTeamType(teamType);

	const { data, loading, error } = useQueryInterop<
		useNewUserTemplatesGetMultipleTemplateInfoQuery,
		useNewUserTemplatesGetMultipleTemplateInfoQueryVariables
	>(templateInfo, {
		variables: {
			templateId0: templates[0].blueprintModuleCompleteKey,
			templateId1: templates[1].blueprintModuleCompleteKey,
			templateId2: templates[2].blueprintModuleCompleteKey,
		},
		skip: skip || teamTypeLoading,
	});
	if (shouldShowLoading || teamTypeLoading || loading) {
		return { loading: true, templates: null };
	}

	if (shouldShowError || teamTypeError || error) {
		return {
			loading: false,
			templates: null,
			error: shouldShowError || teamTypeError || error,
		};
	}

	if (!showNewUserTemplates) {
		return { loading: false, templates: null };
	}

	if (!(data?.template0 && data?.template1 && data?.template2)) {
		return { loading: false, templates: null };
	}

	return {
		loading: false,
		templates: [
			{
				...data.template0,
				name: formatMessage(templates[0].name),
			},
			{
				...data.template1,
				name: formatMessage(templates[1].name),
			},
			{
				...data.template2,
				name: formatMessage(templates[2].name),
			},
		],
	};
};
